import { supabase } from './supabase';

export interface ProfileCardData {
  full_name: string;
  email: string;
  phone_number: string;
  street_address: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  company_name: string;
  job_title: string;
  additional_notes: string;
}

export interface ProfileCard {
  id: string;
  user_id: string;
  card_name: string;
  is_default: boolean;
  profile_data: ProfileCardData;
  created_at: string;
  updated_at: string;
}

export interface CreateProfileCardInput {
  card_name: string;
  is_default?: boolean;
  profile_data: ProfileCardData;
}

export interface UpdateProfileCardInput {
  card_name?: string;
  is_default?: boolean;
  profile_data?: ProfileCardData;
}

export const profileCardService = {
  async getAll(userId: string): Promise<{ data: ProfileCard[] | null; error: Error | null }> {
    try {
      if (!userId || userId === 'null' || userId === 'undefined') {
        throw new Error('Invalid user ID');
      }

      const { data, error } = await supabase
        .from('user_profile_cards')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching profile cards:', error);
      return { data: null, error: error as Error };
    }
  },

  async getById(id: string, userId: string): Promise<{ data: ProfileCard | null; error: Error | null }> {
    try {
      if (!userId || userId === 'null' || userId === 'undefined') {
        throw new Error('Invalid user ID');
      }

      const { data, error } = await supabase
        .from('user_profile_cards')
        .select('*')
        .eq('id', id)
        .eq('user_id', userId)
        .maybeSingle();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching profile card:', error);
      return { data: null, error: error as Error };
    }
  },

  async getDefault(userId: string): Promise<{ data: ProfileCard | null; error: Error | null }> {
    try {
      if (!userId || userId === 'null' || userId === 'undefined') {
        throw new Error('Invalid user ID');
      }

      const { data, error } = await supabase
        .from('user_profile_cards')
        .select('*')
        .eq('user_id', userId)
        .eq('is_default', true)
        .maybeSingle();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error fetching default profile card:', error);
      return { data: null, error: error as Error };
    }
  },

  async create(userId: string, input: CreateProfileCardInput): Promise<{ data: ProfileCard | null; error: Error | null }> {
    try {
      if (!userId || userId === 'null' || userId === 'undefined') {
        throw new Error('Invalid user ID');
      }

      const { data, error } = await supabase
        .from('user_profile_cards')
        .insert([
          {
            user_id: userId,
            card_name: input.card_name,
            is_default: input.is_default || false,
            profile_data: input.profile_data,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error creating profile card:', error);
      return { data: null, error: error as Error };
    }
  },

  async update(id: string, userId: string, input: UpdateProfileCardInput): Promise<{ data: ProfileCard | null; error: Error | null }> {
    try {
      if (!userId || userId === 'null' || userId === 'undefined') {
        throw new Error('Invalid user ID');
      }

      const updateData: any = {};

      if (input.card_name !== undefined) {
        updateData.card_name = input.card_name;
      }

      if (input.is_default !== undefined) {
        updateData.is_default = input.is_default;
      }

      if (input.profile_data !== undefined) {
        updateData.profile_data = input.profile_data;
      }

      const { data, error } = await supabase
        .from('user_profile_cards')
        .update(updateData)
        .eq('id', id)
        .eq('user_id', userId)
        .select()
        .single();

      if (error) throw error;

      return { data, error: null };
    } catch (error) {
      console.error('Error updating profile card:', error);
      return { data: null, error: error as Error };
    }
  },

  async delete(id: string, userId: string): Promise<{ error: Error | null }> {
    try {
      if (!userId || userId === 'null' || userId === 'undefined') {
        throw new Error('Invalid user ID');
      }

      const { error } = await supabase
        .from('user_profile_cards')
        .delete()
        .eq('id', id)
        .eq('user_id', userId);

      if (error) throw error;

      return { error: null };
    } catch (error) {
      console.error('Error deleting profile card:', error);
      return { error: error as Error };
    }
  },

  formatForDocument(card: ProfileCard): string {
    const data = card.profile_data;
    const lines: string[] = [];

    if (data.full_name) {
      lines.push(data.full_name);
    }

    if (data.company_name || data.job_title) {
      const professional = [data.job_title, data.company_name].filter(Boolean).join(' at ');
      if (professional) lines.push(professional);
    }

    if (data.street_address) {
      lines.push(data.street_address);
    }

    const cityStateLine = [data.city, data.state, data.zip_code].filter(Boolean).join(', ');
    if (cityStateLine) {
      lines.push(cityStateLine);
    }

    if (data.country) {
      lines.push(data.country);
    }

    if (data.email) {
      lines.push(`Email: ${data.email}`);
    }

    if (data.phone_number) {
      lines.push(`Phone: ${data.phone_number}`);
    }

    if (data.additional_notes) {
      lines.push('');
      lines.push(data.additional_notes);
    }

    return lines.join('\n');
  },

  detectPlaceholders(content: string): string[] {
    const placeholderPatterns = [
      /\[([^\]]+)\]/g,
      /\{([^}]+)\}/g,
      /\{\{([^}]+)\}\}/g,
      /\(([^)]+)\)/g,
    ];

    const foundPlaceholders = new Set<string>();

    placeholderPatterns.forEach(pattern => {
      const matches = content.matchAll(pattern);
      for (const match of matches) {
        foundPlaceholders.add(match[0]);
      }
    });

    return Array.from(foundPlaceholders);
  },

  getFieldValue(card: ProfileCard, fieldKey: string): string {
    const data = card.profile_data;
    const normalizedKey = fieldKey.toLowerCase().trim();

    const fieldMap: Record<string, () => string> = {
      'full name': () => data.full_name || '',
      'your full name': () => data.full_name || '',
      'name': () => data.full_name || '',
      'your name': () => data.full_name || '',

      'phone': () => data.phone_number || '',
      'phone number': () => data.phone_number || '',
      'contact number': () => data.phone_number || '',
      'mobile': () => data.phone_number || '',
      'telephone': () => data.phone_number || '',

      'email': () => data.email || '',
      'email address': () => data.email || '',
      'your email': () => data.email || '',
      'e-mail': () => data.email || '',

      'location': () => {
        const parts = [data.city, data.state].filter(Boolean);
        return parts.join(', ') || '';
      },
      'city, state': () => {
        const parts = [data.city, data.state].filter(Boolean);
        return parts.join(', ') || '';
      },
      'city': () => data.city || '',
      'state': () => data.state || '',
      'zip': () => data.zip_code || '',
      'zip code': () => data.zip_code || '',
      'postal code': () => data.zip_code || '',
      'country': () => data.country || '',

      'address': () => data.street_address || '',
      'street address': () => data.street_address || '',
      'full address': () => {
        const parts = [
          data.street_address,
          [data.city, data.state, data.zip_code].filter(Boolean).join(', '),
          data.country
        ].filter(Boolean);
        return parts.join('\n') || '';
      },

      'company': () => data.company_name || '',
      'company name': () => data.company_name || '',
      'organization': () => data.company_name || '',

      'job title': () => data.job_title || '',
      'title': () => data.job_title || '',
      'position': () => data.job_title || '',
      'role': () => data.job_title || '',
    };

    const getter = fieldMap[normalizedKey];
    return getter ? getter() : '';
  },

  replaceInlinePlaceholders(content: string, card: ProfileCard): { content: string; replacedCount: number } {
    let replacedCount = 0;
    let result = content;

    const inlinePattern = /\[Phone Number\]\s*\|\s*\[Email\]\s*\|\s*\[Location\]/gi;
    const inlineMatch = result.match(inlinePattern);

    if (inlineMatch) {
      const data = card.profile_data;
      const phone = data.phone_number || '[Phone Number]';
      const email = data.email || '[Email]';
      const location = [data.city, data.state].filter(Boolean).join(', ') || '[Location]';

      const replacement = `${phone} | ${email} | ${location}`;
      result = result.replace(inlinePattern, replacement);
      replacedCount++;
    }

    const placeholderPatterns = [
      { regex: /\[([^\]]+)\]/g, wrapper: ['[', ']'] },
      { regex: /\{\{([^}]+)\}\}/g, wrapper: ['{{', '}}'] },
      { regex: /\{([^}]+)\}/g, wrapper: ['{', '}'] },
    ];

    placeholderPatterns.forEach(({ regex, wrapper }) => {
      const matches = Array.from(result.matchAll(regex));

      matches.forEach(match => {
        const fullMatch = match[0];
        const fieldKey = match[1];

        if (fullMatch.includes('|')) {
          return;
        }

        const value = this.getFieldValue(card, fieldKey);

        if (value) {
          result = result.replace(fullMatch, value);
          replacedCount++;
        }
      });
    });

    return { content: result, replacedCount };
  },

  replacePlaceholders(content: string, card: ProfileCard): { content: string; replacedCount: number; message: string } {
    const placeholders = this.detectPlaceholders(content);

    if (placeholders.length === 0) {
      return {
        content,
        replacedCount: 0,
        message: 'No placeholders found in document'
      };
    }

    const { content: newContent, replacedCount } = this.replaceInlinePlaceholders(content, card);

    const message = replacedCount > 0
      ? `Successfully replaced ${replacedCount} placeholder${replacedCount !== 1 ? 's' : ''}`
      : 'No matching placeholders found for your profile data';

    return {
      content: newContent,
      replacedCount,
      message
    };
  },
};
